/* RESPONSIBLE TEAM: team-conversational-knowledge */

import type Store from '@ember-data/store';
import { action } from '@ember/object';
import Service, { inject as service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import type { Block } from '@intercom/interblocks.ts';
import type { Task, TaskGenerator } from 'ember-concurrency';
import { dropTask, task } from 'ember-concurrency-decorators';
import { type BlockGroup } from 'embercom/components/knowledge-hub/content-editor/shared/suggestion-editor';
import { post } from 'embercom/lib/ajax';
import type ArticleContent from 'embercom/models/articles/article-content';
import type ContentReviewRequest from 'embercom/models/content-service/content-review-request';
import { BlockEditType, ReviewType } from 'embercom/models/content-service/content-review-request';
import type ContentSnippet from 'embercom/models/content-service/content-snippet';
import { Status } from 'embercom/models/data/content-service/content-review-statuses';
import { EntityType } from 'embercom/models/data/entity-types';
import type KnowledgeHubEditorService from './knowledge-hub-editor-service';
import type ContentSuggestionsService from 'embercom/services/content-suggestions-service';
import type IntlService from 'ember-intl/services/intl';
import type ContentImportService from './content-import-service';
import { taskFor } from 'ember-concurrency-ts';
import { schedule } from '@ember/runloop';
import { positionSuggestionCardsWithoutOverlap } from 'embercom/components/knowledge-hub/content-editor/shared/utils/suggestion-card-helper';
import type RouterService from '@ember/routing/router-service';
import RouteRegexes from 'embercom/lib/route-regexes';
import type KnowledgeHubService from './knowledge-hub-service';
import { ContentAction } from 'embercom/objects/knowledge-hub/localized-knowledge-content';
import { get } from 'embercom/lib/ajax';
import { captureException } from 'embercom/lib/sentry';
import type ContentSectionRelationship from 'embercom/models/content-service/content-section-relationship';
import { CAN_MANAGE_KNOWLEDGE_BASE_CONTENT } from 'embercom/lib/knowledge-hub/constants';
import { timeout } from 'ember-concurrency';
import { restartableTask } from 'ember-concurrency-decorators';
import ENV from 'embercom/config/environment';
import type KnowledgeHubDrawerEditorService from './knowledge-hub-drawer-editor-service';

export interface RelatedContentEntity {
  entityId: number;
  entityType: EntityType;
  title?: string;
  lastUpdatedBy?: string;
  previewText?: string;
  updatedAt?: string;
}

export type ReviewableContent = ArticleContent | ContentSnippet | ContentSectionRelationship;

export default class SuggestionService extends Service {
  @service declare appService: $TSFixMe;
  @service declare store: Store;
  @service declare knowledgeHubEditorService: KnowledgeHubEditorService;
  @service declare knowledgeHubDrawerEditorService: KnowledgeHubDrawerEditorService;
  @service declare contentSuggestionsService: ContentSuggestionsService;
  @service declare notificationsService: $TSFixMe;
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare permissionsService: $TSFixMe;
  @service declare contentImportService: ContentImportService;
  @service declare knowledgeHubService: KnowledgeHubService;

  @tracked blockGroups: BlockGroup[] = [];
  @tracked blockGroupsForEditing: { [key: string]: Block[] } = {};
  @tracked hasUnsavedChanges = false;
  @tracked editedTitle: string | null = null;
  @tracked editedSummary: string | null = null;

  @task
  *startReplaceReviewRequest(
    reviewRequestId: string,
    entityId: number,
    entityType: EntityType,
  ): TaskGenerator<void> {
    yield post(`/ember/content_service/content_review_requests/${reviewRequestId}/start_replace`, {
      app_id: this.appService.app.id,
      target_suggestion_type: 1, // edit_content type
      target_entity_type: entityType,
      target_entity_id: entityId,
    });
  }

  @task
  *completeReviewRequest(
    reviewRequestId: string,
    entityId: number,
    entityType: EntityType,
  ): TaskGenerator<void> {
    let reviewRequestsResponse = yield post(
      `/ember/content_service/content_review_requests/${reviewRequestId}/complete_replace`,
      {
        app_id: this.appService.app.id,
        target_suggestion_type: 1, // edit_content type
        target_entity_type: entityType,
        target_entity_id: entityId,
      },
    );

    let isActiveContentResponseContent = this.isActiveContent(entityId, entityType);
    if (isActiveContentResponseContent) {
      // reload content review requests
      let editorActiveContent = this.knowledgeHubEditorService
        .activeContentModel as ReviewableContent;
      let reviewRequests = this.storeReviewRequests(reviewRequestsResponse);
      editorActiveContent.contentReviewRequests.clear();
      editorActiveContent.contentReviewRequests.pushObjects(reviewRequests);
    }

    // reload the suggestions list on the suggestions page
    yield this.contentSuggestionsService.refreshSuggestionsListWithDelay();
  }

  resetEditedState(activeContent: ReviewableContent) {
    this.editedTitle = null;
    this.hasUnsavedChanges = false;
    if (activeContent.entityType === EntityType.ArticleContent) {
      this.editedSummary = null;
    }
  }

  isActiveContent(entityId: number, entityType: EntityType): boolean {
    let editorActiveContent = this.knowledgeHubEditorService
      .activeContentModel as ReviewableContent;
    return (
      editorActiveContent.entityType === entityType &&
      editorActiveContent.id.toString() === entityId.toString()
    );
  }

  @task
  *replaceReviewRequest(
    reviewRequestId: string,
    entityId: number,
    entityType: EntityType,
  ): TaskGenerator<void> {
    try {
      yield (
        this.startReplaceReviewRequest as unknown as Task<void, [string, number, EntityType]>
      ).perform(reviewRequestId, entityId, entityType);

      if (this.isActiveContent(entityId, entityType)) {
        yield (
          this.completeReviewRequest as unknown as Task<void, [string, number, EntityType]>
        ).perform(reviewRequestId, entityId, entityType);
      }
    } catch (error) {
      if (this.isActiveContent(entityId, entityType)) {
        this.notificationsService.notifyError(
          this.intl.t('knowledge-hub.content-editor.review.notification.replaced.error'),
        );
        // If on the active content we're generating suggestions, then we need to leave this page
        history.back();
      }
    }
  }

  @dropTask
  *approveSuggestion(reviewRequest: ContentReviewRequest, entityTypeToApproveAs: EntityType) {
    try {
      yield this.permissionsService.checkPermission(
        this.activeContent.requiredEditPermissionForKnowledgeHub,
      );
    } catch (e) {
      return;
    }

    try {
      reviewRequest.set('updatedSuggestionBlocks', this.editedJsonBlocks);

      reviewRequest.set(
        'title',
        this.editedTitle ?? this.activeContent.latestVersion?.versionTitle,
      );

      reviewRequest.set('status', Status.Approved);
      reviewRequest.set('approveAsEntityType', entityTypeToApproveAs);
      yield reviewRequest.save();
    } catch (error) {
      let errorKey = reviewRequest.isUnderperformingContent
        ? 'approved-underperforming-content'
        : 'approved';
      this.notificationsService.notifyError(
        this.intl.t(`knowledge-hub.content-editor.review.notification.${errorKey}.error`),
      );
    }
    this.hasDirtyContent = false;
  }

  @dropTask
  *rejectSuggestion(reviewRequest: ContentReviewRequest) {
    try {
      yield this.permissionsService.checkPermission(
        this.activeContent.requiredEditPermissionForKnowledgeHub,
      );
    } catch (e) {
      return;
    }

    try {
      reviewRequest.set('status', Status.Rejected);
      yield reviewRequest.save();
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.content-editor.review.notification.rejected.error'),
      );
    }
    this.hasDirtyContent = false;
  }

  private storeReviewRequests(reviewRequests: any[]): ContentReviewRequest[] {
    return reviewRequests.map((reviewRequest) => {
      let normalizedReviewRequest = this.store.normalize(
        'content-service/content-review-request',
        reviewRequest,
      );
      return this.store.push(normalizedReviewRequest) as ContentReviewRequest;
    });
  }

  private getJsonBlocks(activeContent: ReviewableContent): Block[] {
    return activeContent.entityType === EntityType.ContentSnippet
      ? activeContent.serializedBlocks
      : (activeContent.jsonBlocks as Block[]);
  }

  get editedJsonBlocks() {
    return this.blockGroups.flatMap((blockGroup) => {
      if (
        blockGroup.reviewRequest?.get('status') === Status.Rejected ||
        blockGroup.reviewRequest?.get('status') === Status.Pending
      ) {
        if (
          blockGroup.reviewRequest?.get('reviewType') === ReviewType.CREATE_CONTENT ||
          blockGroup.reviewRequest?.get('reviewType') === ReviewType.UNDERPERFORMING_CONTENT
        ) {
          return blockGroup.blocks;
        }
        return blockGroup.blocksBefore ?? [];
      }

      return blockGroup.blocks;
    });
  }

  set title(editedTitle: string | null) {
    this.editedTitle = editedTitle;
  }

  set summary(editedSummary: string | null) {
    this.editedSummary = editedSummary;
  }

  set hasDirtyContent(value: boolean) {
    this.hasUnsavedChanges = value;
  }

  get hasDirtyContent() {
    return this.hasUnsavedChanges;
  }

  get contentTitle() {
    return this.editedTitle ?? this.activeContent.latestVersion?.versionTitle;
  }

  get contentSummary() {
    let activeContent = this.activeContent as ArticleContent;
    return this.editedSummary ?? activeContent.latestVersion?.summary;
  }

  get numberOfPendingSuggestions() {
    return this.blockGroups.filter(
      (blockGroup) => blockGroup.reviewRequest?.get('status') === Status.Pending,
    ).length;
  }

  @action
  createBlockGroups(
    activeContent: ReviewableContent,
    reviewRequests: ContentReviewRequest[],
  ): BlockGroup[] {
    let editedBlockCounter = 0;
    let uneditedBlockCounter = 0;
    let blockGroups: BlockGroup[] = [];
    let currentIndex = 0;
    let jsonBlocks = this.getJsonBlocks(activeContent);

    this.blockGroupsForEditing = {};

    let sortedRequests = this.getSortedReviewRequests(reviewRequests);

    for (let request of sortedRequests) {
      if (
        request.get('reviewType') === ReviewType.CREATE_CONTENT ||
        request.isUnderperformingContent
      ) {
        this.handleCreateContentRequest(blockGroups, request, jsonBlocks);
        // Set currentIndex to the end of the jsonBlocks array because we only have one suggestion for create content
        currentIndex = jsonBlocks.length + 1;
      } else if (request.isContradictingContent || request.isDuplicateContent) {
        this.handleRemainingUneditedBlocks(
          blockGroups,
          jsonBlocks,
          currentIndex,
          uneditedBlockCounter,
        );
      } else {
        let result = this.handleEditContentRequest(
          blockGroups,
          request,
          jsonBlocks,
          currentIndex,
          uneditedBlockCounter,
          editedBlockCounter,
        );

        currentIndex = result.currentIndex;
        uneditedBlockCounter = result.uneditedBlockCounter;
        editedBlockCounter = result.editedBlockCounter;
      }
    }

    // Add any remaining unedited blocks after the last suggestion
    this.handleRemainingUneditedBlocks(blockGroups, jsonBlocks, currentIndex, uneditedBlockCounter);

    this.blockGroups = blockGroups;
    return blockGroups;
  }

  private getSortedReviewRequests(reviewRequests: ContentReviewRequest[]): ContentReviewRequest[] {
    let validRequests = reviewRequests.filter((request) => !isEmpty(request.get('suggestion')));

    // Sort requests by suggestion start index
    if (validRequests.length > 0) {
      return [...validRequests].sort(
        (a, b) =>
          (a.get('suggestion')!.indices?.[0] ?? 0) - (b.get('suggestion')!.indices?.[0] ?? 0),
      );
    }
    return validRequests;
  }

  private handleCreateContentRequest(
    blockGroups: BlockGroup[],
    request: ContentReviewRequest,
    jsonBlocks: Block[],
  ) {
    blockGroups.push({
      id: 'unedited-block-group-0',
      isEdited: false,
      blocks: jsonBlocks,
      reviewRequest: request,
    });
  }

  private handleEditContentRequest(
    blockGroups: BlockGroup[],
    request: ContentReviewRequest,
    jsonBlocks: Block[],
    currentIndex: number,
    uneditedBlockCounter: number,
    editedBlockCounter: number,
  ) {
    let suggestion = request.get('suggestion')!;
    let [startIndex, endIndex] = suggestion.indices;

    // Add unedited blocks before the suggestion if any
    if (currentIndex < startIndex) {
      let blocksForGroup = jsonBlocks.slice(currentIndex, startIndex);

      blockGroups.push({
        id: `unedited-block-group-${uneditedBlockCounter++}`,
        isEdited: false,
        blocks: blocksForGroup,
        blocksBefore: blocksForGroup,
      });
    }

    let reviewedBlocks = jsonBlocks.slice(startIndex, endIndex + 1);

    if (suggestion.blockEditType === BlockEditType.APPEND_NEW_BLOCKS) {
      // First add the referenced blocks as unedited
      blockGroups.push({
        isEdited: false,
        id: `unedited-block-group-${uneditedBlockCounter++}`,
        blocks: reviewedBlocks,
        blocksBefore: reviewedBlocks,
      });

      // Then add the new blocks as a new group after the end index
      blockGroups.push({
        isEdited: true,
        id: `edited-block-group-${editedBlockCounter++}`,
        blocks: suggestion.editedBlocks,
        referencedBlocks: reviewedBlocks,
        blocksBefore: [], // Empty blocksBefore since this is an append
        reviewRequest: request,
      });
    } else {
      blockGroups.push({
        isEdited: true,
        id: `edited-block-group-${editedBlockCounter++}`,
        blocks: suggestion.editedBlocks,
        blocksBefore: reviewedBlocks,
        reviewRequest: request,
      });
    }

    currentIndex = endIndex + 1;
    return { currentIndex, uneditedBlockCounter, editedBlockCounter };
  }

  private handleRemainingUneditedBlocks(
    blockGroups: BlockGroup[],
    jsonBlocks: Block[],
    currentIndex: number,
    uneditedBlockCounter: number,
  ) {
    let blocksAfterSuggestions = jsonBlocks.slice(currentIndex);

    if (currentIndex < jsonBlocks.length) {
      blockGroups.push({
        isEdited: false,
        id: `unedited-block-group-${uneditedBlockCounter++}`,
        blocks: blocksAfterSuggestions,
        blocksBefore: blocksAfterSuggestions,
      });
    }
  }

  /**
   * Updates the indices of block groups that have been edited to maintain correct positioning
   * in the content. This is crucial for tracking where suggestions were made in the content
   * after edits have been applied.
   */
  getSuggestionsWithChangedIndices(): {
    content_review_request_id: string;
    updated_indices: number[];
  }[] {
    let updatedSuggestionIndices = [];

    // Iterate through each block group to check if it needs index updates
    for (let blockGroup of this.blockGroups) {
      // Only process block groups that have been edited
      if (blockGroup.isEdited) {
        let reviewRequest = blockGroup.reviewRequest;
        let reviewRequestStatus = reviewRequest?.get('status');
        let suggestion = reviewRequest?.get('suggestion');

        // Only update indices if the suggestion is not rejected
        if (suggestion && reviewRequestStatus !== Status.Rejected) {
          let previousStartIndex = suggestion.indices[0];
          let previousEndIndex = suggestion.indices[1];

          let currentStartIndex;
          let currentEndIndex;

          let blockGroupStartIndex = this.indexOfLastBlockFromPreviousGroup(blockGroup);
          if (suggestion.blockEditType === BlockEditType.APPEND_NEW_BLOCKS) {
            // For appended blocks, both start and end indices are the same
            // as they're added at the end of the referenced block
            currentEndIndex = currentStartIndex = blockGroupStartIndex;
          } else {
            currentStartIndex = blockGroupStartIndex + 1;
            // Using blocksBefore here because we need to track the positions for the blocks being replaced
            currentEndIndex = currentStartIndex + (blockGroup.blocksBefore?.length || 0) - 1;
          }

          // Only update if the indices have actually changed
          if (previousStartIndex !== currentStartIndex || previousEndIndex !== currentEndIndex) {
            // Update the suggestion indices
            suggestion.indices = [Math.max(currentStartIndex, 0), Math.max(currentEndIndex, 0)];
            blockGroup.reviewRequest?.set('suggestion', suggestion);

            // Add the updated indices to our tracking array
            if (blockGroup.reviewRequest?.get('id')) {
              updatedSuggestionIndices.push({
                content_review_request_id: blockGroup.reviewRequest.get('id'),
                updated_indices: suggestion.indices,
              });
            }
          }
        }
      }
    }
    return updatedSuggestionIndices;
  }

  private indexOfLastBlockFromPreviousGroup(blockGroup: BlockGroup) {
    let previousBlockGroup = this.getPreviousBlockGroup(blockGroup);
    if (!previousBlockGroup) {
      // return -1 if there is no previous block group
      return -1;
    }
    let previousBlockGroupBlocks = this.activeBlockList(previousBlockGroup);
    let lastBlockInPreviousBlockGroup =
      previousBlockGroupBlocks[previousBlockGroupBlocks.length - 1];
    return this.editedJsonBlocks.indexOf(lastBlockInPreviousBlockGroup);
  }

  private getPreviousBlockGroup(blockGroup: BlockGroup) {
    let blockGroupPosition = this.blockGroups.findIndex(
      (currentGroup) => currentGroup.id === blockGroup.id,
    );
    if (blockGroupPosition === 0) {
      // If the block group is the first one, then there is no previous block group
      return null;
    }
    return this.blockGroups[blockGroupPosition - 1];
  }

  private activeBlockList(blockGroup: BlockGroup): Block[] {
    let isPending = blockGroup.reviewRequest?.get('status') === Status.Pending;

    if (isPending) {
      return blockGroup.blocksBefore!;
    }

    return blockGroup.blocks;
  }

  get rejectedContentReviewRequestIds() {
    return this.blockGroups
      .filter((blockGroup) => blockGroup.reviewRequest?.get('status') === Status.Rejected)
      .map((blockGroup) => blockGroup.reviewRequest?.get('id'));
  }

  get approvedContentReviewRequestIds() {
    return this.blockGroups
      .filter((blockGroup) => blockGroup.reviewRequest?.get('status') === Status.Approved)
      .map((blockGroup) => blockGroup.reviewRequest?.get('id'));
  }

  get activeContent() {
    return this.knowledgeHubEditorService.reviewableActiveContent;
  }

  get reviewRequests() {
    return this.activeContent.contentReviewRequests;
  }

  get pendingReviewRequests() {
    return this.reviewRequests.filter((request) => request.get('status') === Status.Pending);
  }

  get allReviewRequestsAreActioned() {
    return this.reviewRequests.every((request) => request.get('status') !== Status.Pending);
  }

  get isOnAiInsightsPage() {
    return RouteRegexes.automation.analyze.test(this.router.currentRouteName);
  }

  repositionSuggestionCards() {
    schedule('afterRender', () => {
      positionSuggestionCardsWithoutOverlap(this.blockGroups.filter((group) => group.isEdited));
    });
  }

  async reloadContent(options: { isSaveAsDraft?: boolean; isCancel?: boolean }) {
    if (options.isCancel) {
      if (this.activeContent && this.activeContent.contentReviewRequests) {
        this.activeContent.contentReviewRequests.forEach((reviewRequest) => {
          reviewRequest.rollbackAttributes();
        });
      }
    }
    await this.knowledgeHubEditorService.reloadActiveContent(ContentAction.EDIT);
    this.createBlockGroups(this.activeContent, this.pendingReviewRequests);
    this.hasDirtyContent = false;
    //we only repositon cards after saving as draft
    if (options.isSaveAsDraft) {
      this.repositionSuggestionCards();
    }
  }

  @dropTask
  *saveSnippetTask() {
    try {
      yield this.permissionsService.checkPermission(
        this.activeContent.requiredEditPermissionForKnowledgeHub,
      );
    } catch (e) {
      return;
    }
    yield taskFor(this.performSaveAction).perform();
  }

  @dropTask
  *saveArticleAsDraftTask() {
    try {
      yield this.permissionsService.checkPermission(
        this.activeContent.requiredEditPermissionForKnowledgeHub,
      );
    } catch (e) {
      return;
    }
    yield taskFor(this.performSaveAction).perform('save_draft');
  }

  @dropTask
  *publishArticleContentTask() {
    try {
      yield this.permissionsService.checkPermission(
        this.activeContent.requiredPublishPermissionForKnowledgeHub,
      );
    } catch (e) {
      return;
    }
    yield taskFor(this.performSaveAction).perform('approve_and_publish');
  }

  @dropTask
  *performSaveAction(reviewAction?: 'save_draft' | 'approve_and_publish') {
    try {
      let updatedSuggestionIndices = this.getSuggestionsWithChangedIndices();

      let payload: any = {
        app_id: this.appService.app.id,
        entity_id: this.activeContent.id,
        entity_type: this.activeContent.entityType,
        rejected_content_review_request_ids: Array.from(this.rejectedContentReviewRequestIds),
        approved_content_review_request_ids: Array.from(this.approvedContentReviewRequestIds),
        json_blocks: this.editedJsonBlocks,
        title: this.contentTitle,
      };

      if (this.knowledgeHubEditorService.activeContentIsPublicArticle) {
        payload.summary = this.contentSummary;
        payload.review_action = reviewAction;
      }

      if (updatedSuggestionIndices.length > 0) {
        payload.updated_content_review_request_suggestion_indices = updatedSuggestionIndices;
      }

      yield post('/ember/content_service/content_review_requests/bulk_update', payload);
      this.hasDirtyContent = false;
    } catch (error) {
      let message =
        reviewAction === 'save_draft'
          ? this.intl.t('knowledge-hub.content-editor.review.notification.saved.error')
          : this.intl.t('knowledge-hub.content-editor.review.notification.published.error');
      this.notificationsService.notifyError(message);
    }
  }

  @restartableTask
  *autoSaveContentTask() {
    if (this.activeContent.entityType === EntityType.ContentSnippet) {
      try {
        yield this.permissionsService.checkPermission(CAN_MANAGE_KNOWLEDGE_BASE_CONTENT);
      } catch (e) {
        return;
      }
      yield timeout(ENV.APP._5000MS);
      if (
        this.knowledgeHubDrawerEditorService.isClosingDrawer ||
        taskFor(this.approveSuggestion).isRunning ||
        taskFor(this.rejectSuggestion).isRunning
      ) {
        return;
      }
      yield taskFor(this.performSaveSnippetContent).perform({
        isAutosavingContent: true,
        isSavingContent: false,
      });
      yield this.reloadContent({ isSaveAsDraft: false });
    }
  }

  @dropTask
  *performSaveSnippetContent(options: { isAutosavingContent: boolean; isSavingContent: boolean }) {
    try {
      let payload: any = {
        app_id: this.appService.app.id,
        entity_id: this.knowledgeHubEditorService.activeContentModel.id,
        entity_type: this.knowledgeHubEditorService.activeContentModel.entityType,
        json_blocks: this.editedJsonBlocks,
        is_autosaving_content: options.isAutosavingContent,
        is_saving_content: options.isSavingContent,
        title: this.contentTitle,
      };

      yield post('/ember/content_service/content_review_requests/bulk_update', payload);
      this.hasDirtyContent = false;
    } catch (error) {
      let message = options.isAutosavingContent
        ? this.intl.t('knowledge-hub.content-editor.review.notification.autosave.error')
        : this.intl.t('knowledge-hub.content-editor.review.notification.saved.error');
      this.notificationsService.notifyError(message);
    }
  }

  async loadRelatedContent(reviewRequestId: string): Promise<RelatedContentEntity[]> {
    try {
      let response = await get(
        `/ember/content_service/content_review_requests/${reviewRequestId}/related_content`,
        {
          top_k: 10,
          app_id: this.appService.app.id,
          include_content_details: true,
        },
      );

      return response.map((entity: any) => {
        return {
          entityId: Number(entity.entity_id),
          entityType: entity.entity_type as EntityType,
          title: entity.title,
          lastUpdatedBy: entity.last_updated_by,
          previewText: entity.preview_text,
          updatedAt: entity.updated_at,
        };
      });
    } catch (error) {
      captureException(error, {
        fingerprint: ['suggestion-service', 'failed-to-load-related-content'],
        extra: {
          reviewRequestId,
          appId: this.appService.app.id,
        },
        tags: {
          responsibleTeam: 'team-conversational-knowledge',
          responsible_team: 'team-conversational-knowledge',
        },
      });
      return [];
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    SuggestionService: SuggestionService;
    'suggestion-service': SuggestionService;
  }
}
